import { Notification } from "@screencloud/screencloud-ui-components";
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { formatted } from "src/billinglatest/util/currency";
import type { PixiAddress, StepProps } from "../../hooks/usePixiWizardState";
import { DISCOUNT_PERCENT } from "../PlanBillingStep/constants";
import { OrderSummary } from "./OrderSummary";
import { ReviewCard } from "./ReviewCard";
import { MOCK_BILLING_ADDRESS, MOCK_SHIPPING_ADDRESS } from "./mockReviewData";
import { useCheckoutPricing } from "./useCheckoutPricing";
import { usePurchase } from "./usePurchase";
import {
  AddressBlock,
  AddressGroup,
  AddressLabel,
  AddressText,
  CheckoutLayout,
  Label,
  MutedRow,
  ReviewColumn,
  Row,
  SummaryColumn,
  Value,
} from "./styles";

const NUMBER_OF_SCREENS_STEP = 1;
const PLAN_BILLING_STEP = 2;
const PAYMENT_SHIPPING_STEP = 3;

const addressLines = (address?: PixiAddress): string[] => {
  if (!address) return [];
  const name = [address.firstName, address.secondName]
    .filter(Boolean)
    .join(" ");
  const cityLine = [address.city, address.state, address.postalCode]
    .filter(Boolean)
    .join(", ");
  const streetLine = [address.building, address.street]
    .filter(Boolean)
    .join(" ");
  return [name, address.company, streetLine, cityLine, address.country].filter(
    (line): line is string => Boolean(line),
  );
};

const CheckoutStepComponent = ({
  state,
  updateState,
  goToStep,
  intl,
}: StepProps & InjectedIntlProps) => {
  const { purchase, loading: purchasing } = usePurchase();

  const {
    planContent,
    currency,
    billingCycle,
    licenses,
    shippingCountry,
    isFreeDevice,
    isDiscount,
    licencesCents,
    deviceSavingsCents,
    discountCents,
    shippingCents,
    grandTotalCents,
  } = useCheckoutPricing(state);

  // Payment & Shipping step is a stub on `main`; fall back to mocked addresses
  // mirroring #13423 so the review card has content until that step ships.
  const billingAddress = state.billingAddress ?? MOCK_BILLING_ADDRESS;
  const shippingAddress = state.useSameAddressForShipping
    ? billingAddress
    : (state.shippingAddress ?? MOCK_SHIPPING_ADDRESS);

  const price = (cents: number) => formatted(currency, cents);
  const editLabel = intl.formatMessage({
    id: "pixi_wizard.checkout.edit",
    defaultMessage: "Edit",
  });
  const billingType = intl.formatMessage(
    billingCycle === "year"
      ? { id: "pixi_wizard.checkout.cycle_annual", defaultMessage: "Annual" }
      : { id: "pixi_wizard.checkout.cycle_monthly", defaultMessage: "Monthly" },
  );

  const onPurchase = async () => {
    const ok = await purchase({
      hardware: isFreeDevice
        ? { device: "PIXI", quantity: licenses, shippingCountry }
        : undefined,
      shippingCharge: isFreeDevice
        ? {
            amount: shippingCents,
            currency,
            description: "PIXI hardware shipping",
          }
        : undefined,
    });
    if (ok) {
      // Flip to the Complete (success) screen — see CompleteStep / GRWBKK-1663.
      updateState({ purchased: true });
    } else {
      Notification.error({
        message: intl.formatMessage({
          id: "pixi_wizard.checkout.purchase_error",
          defaultMessage: "Something went wrong. Please try again.",
        }),
      });
    }
  };

  return (
    <CheckoutLayout data-testid="checkout-step">
      <ReviewColumn>
        <ReviewCard
          titleId="pixi_wizard.checkout.screens.title"
          titleDefault="Number of Screens"
          editLabel={editLabel}
          onEdit={() => goToStep?.(NUMBER_OF_SCREENS_STEP)}
          data-testid="review-screens"
        >
          <Row>
            <Label>
              <FormattedMessage
                id="pixi_wizard.checkout.screens.licenses"
                defaultMessage="x{count} Screen Licences"
                values={{ count: licenses }}
              />
            </Label>
            <Value data-testid="review-screens-price">
              {price(licencesCents)}
            </Value>
          </Row>
        </ReviewCard>

        <ReviewCard
          titleId="pixi_wizard.checkout.plan.title"
          titleDefault="Plan & Deals"
          editLabel={editLabel}
          onEdit={() => goToStep?.(PLAN_BILLING_STEP)}
          data-testid="review-plan"
        >
          <Row>
            <Label>
              <FormattedMessage
                id="pixi_wizard.checkout.plan.plan"
                defaultMessage="Plan"
              />
            </Label>
            <Value data-testid="review-plan-name">{planContent.name}</Value>
          </Row>
          <Row>
            <Label>
              <FormattedMessage
                id="pixi_wizard.checkout.plan.billing_type"
                defaultMessage="Billing Type"
              />
            </Label>
            <Value>{billingType}</Value>
          </Row>
          <Row>
            <Label>
              <FormattedMessage
                id="pixi_wizard.checkout.plan.currency"
                defaultMessage="Currency"
              />
            </Label>
            <Value>{currency}</Value>
          </Row>
        </ReviewCard>

        {isFreeDevice && (
          <ReviewCard
            titleId="pixi_wizard.checkout.device.title"
            titleDefault="Device"
            editLabel={editLabel}
            onEdit={() => goToStep?.(PLAN_BILLING_STEP)}
            data-testid="review-device"
          >
            <Row>
              <Label>
                <FormattedMessage
                  id="pixi_wizard.checkout.device.count_label"
                  defaultMessage="Number of devices"
                />
              </Label>
              <Value data-testid="review-device-count">x{licenses}</Value>
            </Row>
            <MutedRow data-testid="review-device-value">
              <FormattedMessage
                id="pixi_wizard.checkout.device.value"
                defaultMessage="x{count} PIXI device {amount}"
                values={{ count: licenses, amount: price(deviceSavingsCents) }}
              />
            </MutedRow>
            <MutedRow data-testid="review-device-offer">
              <FormattedMessage
                id="pixi_wizard.checkout.device.free"
                defaultMessage="Free device -{amount}"
                values={{ amount: price(deviceSavingsCents) }}
              />
            </MutedRow>
            <MutedRow>
              <FormattedMessage
                id="pixi_wizard.checkout.device.shipping"
                defaultMessage="Shipping +{amount}"
                values={{ amount: price(shippingCents) }}
              />
            </MutedRow>
          </ReviewCard>
        )}

        {isDiscount && (
          <ReviewCard
            titleId="pixi_wizard.checkout.discount.title"
            titleDefault="Discount"
            editLabel={editLabel}
            onEdit={() => goToStep?.(PLAN_BILLING_STEP)}
            data-testid="review-discount"
          >
            <Row>
              <Label>
                <FormattedMessage
                  id="pixi_wizard.checkout.discount.amount_label"
                  defaultMessage="Discount amount"
                />
              </Label>
              <Value data-testid="review-discount-percent">
                {DISCOUNT_PERCENT}%
              </Value>
            </Row>
            <MutedRow>
              <FormattedMessage
                id="pixi_wizard.checkout.discount.amount"
                defaultMessage="Savings -{amount}"
                values={{ amount: price(discountCents) }}
              />
            </MutedRow>
          </ReviewCard>
        )}

        <ReviewCard
          titleId="pixi_wizard.checkout.payment.title"
          titleDefault="Payment & Shipping"
          editLabel={editLabel}
          onEdit={() => goToStep?.(PAYMENT_SHIPPING_STEP)}
          data-testid="review-payment"
        >
          <AddressGroup>
            <AddressBlock>
              <AddressLabel>
                <FormattedMessage
                  id="pixi_wizard.checkout.payment.billing"
                  defaultMessage="Billing address"
                />
              </AddressLabel>
              {addressLines(billingAddress).map((line) => (
                <AddressText key={line}>{line}</AddressText>
              ))}
            </AddressBlock>
            <AddressBlock>
              <AddressLabel>
                <FormattedMessage
                  id="pixi_wizard.checkout.payment.shipping"
                  defaultMessage="Shipping information"
                />
              </AddressLabel>
              {addressLines(shippingAddress).map((line) => (
                <AddressText key={line}>{line}</AddressText>
              ))}
            </AddressBlock>
          </AddressGroup>
        </ReviewCard>
      </ReviewColumn>

      <SummaryColumn>
        <OrderSummary
          planName={planContent.name}
          licenses={licenses}
          licensesPrice={price(licencesCents)}
          freeDevices={isFreeDevice}
          deviceCount={licenses}
          devicePrice={price(deviceSavingsCents)}
          shippingPrice={price(shippingCents)}
          total={price(grandTotalCents)}
          purchasing={purchasing}
          onPurchase={onPurchase}
        />
      </SummaryColumn>
    </CheckoutLayout>
  );
};

export const CheckoutStep = injectIntl(CheckoutStepComponent);
